import React, { useRef } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'; // eraser icon

const PRESET_COLORS = [
  '#1a1a1a', // black
  '#e53e3e', // red
  '#3182ce', // blue
  '#38a169', // green
  '#e85d26', // orange
  '#805ad5', // purple
  '#718096', // gray
  '#744210', // brown
  '#d69e2e', // yellow
  '#ffffff', // white
];

/**
 * DrawToolbar — floating toolbar shown in draw mode.
 *
 * Props:
 *   color / onColorChange
 *   size / onSizeChange
 *   erasing / onErasingChange
 *   onUndo
 *   onClear
 */
const DrawToolbar = ({
  color = '#1a1a1a',
  onColorChange,
  size = 2,
  onSizeChange,
  erasing = false,
  onErasingChange,
  onUndo,
  onClear,
}) => {
  const colorInputRef = useRef(null);

  const handleClear = () => {
    if (window.confirm('Clear all strokes? This cannot be undone.')) {
      onClear?.();
    }
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        background: 'rgba(255,255,255,0.97)',
        borderRadius: 3,
        px: 2,
        py: 1.25,
        boxShadow: '0 4px 24px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.08)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(0,0,0,0.06)',
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
    >
      {/* Color presets */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {PRESET_COLORS.map((c) => (
          <Box
            key={c}
            onClick={() => onColorChange?.(c)}
            sx={{
              width: c === color ? 22 : 18,
              height: c === color ? 22 : 18,
              borderRadius: '50%',
              background: c,
              border: c === color
                ? '2.5px solid #1a1a1a'
                : c === '#ffffff'
                  ? '1.5px solid #ddd'
                  : '1.5px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.1s',
              '&:hover': { transform: 'scale(1.2)' },
              flexShrink: 0,
            }}
          />
        ))}

        {/* Custom color input */}
        <Box
          onClick={() => colorInputRef.current?.click()}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
            border: '1.5px solid #ddd',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': { transform: 'scale(1.2)' },
          }}
        />
        <input
          ref={colorInputRef}
          type="color"
          value={color}
          onChange={(e) => onColorChange?.(e.target.value)}
          style={{ display: 'none' }}
        />
      </Box>

      {/* Divider */}
      <Box sx={{ width: 1, height: 28, background: '#eee', flexShrink: 0 }} />

      {/* Size slider + preview dot */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 100 }}>
        <Slider
          min={1}
          max={12}
          value={size}
          onChange={(_, v) => onSizeChange?.(v)}
          size="small"
          sx={{ color: '#1a1a1a', py: 0 }}
        />
        <Box
          sx={{
            width: size + 4,
            height: size + 4,
            borderRadius: '50%',
            background: erasing ? '#aaa' : color,
            flexShrink: 0,
            transition: 'all 0.15s',
          }}
        />
      </Box>

      {/* Divider */}
      <Box sx={{ width: 1, height: 28, background: '#eee', flexShrink: 0 }} />

      {/* Eraser */}
      <Tooltip title={erasing ? 'Eraser (active)' : 'Eraser'}>
        <IconButton
          size="small"
          onClick={() => onErasingChange?.(!erasing)}
          sx={{
            color: erasing ? '#fff' : '#555',
            background: erasing ? '#e85d26' : 'transparent',
            '&:hover': { background: erasing ? '#cf4f1e' : '#f5f5f5' },
            borderRadius: 1.5,
            width: 28,
            height: 28,
          }}
        >
          <AutoFixHighIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Undo */}
      <Tooltip title="Undo last stroke">
        <IconButton
          size="small"
          onClick={onUndo}
          sx={{ color: '#555', '&:hover': { background: '#f5f5f5' }, borderRadius: 1.5 }}
        >
          <UndoIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Clear */}
      <Tooltip title="Clear all">
        <IconButton
          size="small"
          onClick={handleClear}
          sx={{ color: '#ccc', '&:hover': { color: '#e53e3e', background: '#fff5f5' }, borderRadius: 1.5 }}
        >
          <DeleteForeverIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default DrawToolbar;
